<template>
  <div id="permission-sets" class="flex flex-col gap-6 proxima_nova">
    <div v-if="displayView == 'list_view'">
      <div class="flex flex-row justify-between align-center">
        <h1 class="text-xl text_color font-semibold">Permission Sets</h1>
        <a-button class="primary_medium" @click="showRoleModal"
          >Create Role</a-button
        >
      </div>

      <br />
      <SkeletonLoader v-if="isLoadingRoles" :page="loaderPages.CUSTOM_TABLE" />
      <CustomTable
        v-else-if="roles.length > 0"
        :headers="tableHeaders"
        :data="roles"
        :pagination="pagination"
        :custom-row="customRow"
        :show-actions-column="true"
        :actions="actions"
        :custom-column-render="customColumnRender"
        @update:pagination="handlePageChange"
      />

      <EmptyScreen
        v-else
        source="permission-sets"
        text="No roles found"
        description="Create a new role to get started with managing permissions"
        :text-size="16"
        :description-size="14"
      >
        <a-button class="primary_medium" @click="showRoleModal">
          Create Role
        </a-button>
      </EmptyScreen>

      <CustomModal
        v-model:visible="roleModalVisible"
        :title="isEditing ? 'Edit Role' : 'Create Role'"
        width="30vw"
      >
        <div class="form_col">
          <span class="text-sm secondary_color">Role Name</span>
          <a-input
            v-model:value="roleForm.role_name"
            class="input_medium"
            placeholder="Enter role name"
            ref="roleNameInputRef"
            allow-clear
          />
        </div>

        <template #footer>
          <a-button
            class="primary_medium"
            @click="handleRoleModalOk"
            :loading="isLoading"
            :disabled="isLoading"
          >
            {{ isEditing ? "Update" : "Create" }}
          </a-button>
        </template>
      </CustomModal>

      <CustomModal
        v-model:visible="deleteModalVisible"
        title="Delete Role"
        width="32vw"
      >
        <span
          >Do you want to delete this role? This action cannot be undone.</span
        >
        <template #footer>
          <a-button
            class="primary_medium"
            @click="deleteRole"
            :loading="isDeleting"
            :disabled="isDeleting"
          >
            Confirm
          </a-button>
        </template>
      </CustomModal>
    </div>
    <permission-management v-else />
  </div>
</template>

<script setup>
import {
  onMounted,
  reactive,
  ref,
  nextTick,
  watch,
  onUnmounted,
  computed,
} from "vue";
import { message } from "ant-design-vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import DashboardService from "@/src/api/DashboardService.js";
import AuthPermissionService from "@/src/api/AuthPermissionService.js";
import CustomTable from "@/src/components/custom_table_components/CustomTable.vue";
import CustomModal from "@/src/components/CustomModal.vue";
import EmptyScreen from "@/src/components/EmptyScreen.vue";
import AvatarStack from "@/src/components/common/AvatarStack.vue";
import SkeletonLoader from "@/src/components/SkeletonLoader.vue";

import { focusDelay } from "@/src/constants/commonConstants.js";
import { loaderPages } from "@/src/constants/loaderConstants.js";
import PermissionManagement from "./PermissionManagement.vue";
import eventBus from "@/src/utils/eventBus.js";
import { imageNames } from "@/src/constants/imageConstants.js";
import { isABACEnabled } from "@/src/utils/dynamicFormUtils.js";

const router = useRouter();
const store = useStore();
const roleNameInputRef = ref(null);
const roles = ref([]);
const roleModalVisible = ref(false);
const isEditing = ref(false);
const editingRoleId = ref(null);
const deleteModalVisible = ref(false);
const deleteRoleId = ref(null);
const displayView = ref("list_view");
const selectedPermissionId = ref(null);
const isLoading = ref(false);
const isDeleting = ref(false);
const isLoadingRoles = ref(false);

const roleForm = reactive({
  name: "",
});

const pagination = reactive({
  current: 1,
  pageSize: 20,
  total: 0,
});

const handleRowClick = (row) => {
  router.push(`/settings/permissions/${row.id}`);
};

const closePermissionManagement = () => {
  displayView.value = "list_view";
  selectedPermissionId.value = null;
};

const tableHeaders = computed(() => {
  const headers = [{ label: "Role Name", key: "role_name", width: 4 }];

  if (isABACEnabled()) {
    headers.push({ label: "Assigned Users", key: "assigned_users", width: 4 });
  }

  return headers;
});

const customRow = (record) => {
  return {
    onClick: () => {
      handleRowClick(record);
    },
  };
};

const actions = [
  {
    label: "Edit",
    handler: (row) => handleRowClick(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Edit Name",
    handler: (row) => showEditRoleModal(row),
    icon: imageNames.EDIT,
  },
  {
    label: "Delete",
    handler: (row) => showDeleteConfirm(row),
    icon: imageNames.DELETE_GREY,
    disabled: (row) => row.type === "default",
  },
];

const customColumnRender = computed(() => {
  if (!isABACEnabled()) return {};

  return {
    assigned_users: {
      component: AvatarStack,
      props: (row) => ({
        items: row.assigned_users || [],
        maxVisible: 5,
        size: 24,
        fontSize: 10,
        overlap: 10,
      }),
    },
  };
});

const fetchRoles = async () => {
  isLoadingRoles.value = true;
  try {
    let response;

    if (isABACEnabled()) {
      response = await AuthPermissionService.fetchRoles();

      if (response.data) {
        roles.value = response.data.map((role) => ({
          id: role.id,
          role_name: role.attributes?.name,
          description: role.attributes?.description || "",
          type: "custom",
          permissions: [],
          assigned_users: [],
        }));
        pagination.total = response.meta?.total || response.data.length;

        try {
          if (!store.getters["users/users"]) {
            await store.dispatch("users/fetchUsers");
          }

          const allUsers = store.getters["users/users"] || [];

          const membershipsResponse =
            await AuthPermissionService.fetchRoleMemberships();
          if (membershipsResponse?.data) {
            const roleUserMap = {};
            membershipsResponse.data.forEach((membership) => {
              const roleId = membership.attributes.role_id;
              const userId = membership.attributes.user_id;

              const user = allUsers.find((u) => u.id === userId);

              if (!roleUserMap[roleId]) {
                roleUserMap[roleId] = [];
              }

              if (user) {
                roleUserMap[roleId].push({
                  id: user.id,
                  name: user.name || user.email || `User ${user.id}`,
                  email: user.email,
                  first_name: user.first_name,
                  last_name: user.last_name,
                  profile_pic: user.profile_pic,
                  logo_url: user.logo_url,
                });
              }
            });

            roles.value = roles.value.map((role) => ({
              ...role,
              assigned_users: roleUserMap[role.id] || [],
            }));
          }
        } catch (error) {
          console.error("Error fetching role memberships:", error);
        }
      }
    } else {
      response = await DashboardService.fetchRoles();
      if (response.success) {
        roles.value = response.roles;
        pagination.total = response.roles.length;
      }
    }
  } catch (error) {
    console.error("Error fetching roles:", error);
    message.error("Failed to fetch roles");
  } finally {
    isLoadingRoles.value = false;
  }
};

const handlePageChange = (newPagination) => {
  pagination.current = newPagination.current;
  pagination.pageSize = newPagination.pageSize;
  fetchRoles();
};

const showRoleModal = () => {
  isEditing.value = false;
  editingRoleId.value = null;
  roleForm.role_name = "";
  roleForm.permissions = [];
  roleModalVisible.value = true;
};

const showEditRoleModal = (role) => {
  if (role.type === "default") {
    message.warning("Default roles cannot be edited");
    return;
  }

  isEditing.value = true;
  editingRoleId.value = role.id;
  roleForm.role_name = role.role_name;
  roleForm.permissions = role.permissions;
  roleModalVisible.value = true;
};

const handleRoleModalOk = async () => {
  if (!roleForm.role_name) {
    message.error("Role name is required");
    return;
  }

  isLoading.value = true;
  try {
    let response;
    let success = false;

    if (isABACEnabled()) {
      // Use new Authz service
      const payload = {
        name: roleForm.role_name,
        description: "",
      };

      if (isEditing.value) {
        response = await AuthPermissionService.updateRole(
          editingRoleId.value,
          payload
        );
      } else {
        response = await AuthPermissionService.createRole(payload);
      }

      // Authz API returns JSON:API format with data object
      success = !!response.data;
    } else {
      // Use old DashboardService
      const payload = {
        name: roleForm.role_name,
        permissions: roleForm.permissions,
      };

      if (isEditing.value) {
        response = await DashboardService.updateRole(
          editingRoleId.value,
          payload
        );
      } else {
        response = await DashboardService.createRole(payload);
      }

      success = response.success;
    }

    if (success) {
      message.success(
        `Role ${isEditing.value ? "updated" : "created"} successfully`
      );
      roleModalVisible.value = false;
      await fetchRoles();
    } else {
      message.error(`Failed to ${isEditing.value ? "update" : "create"} role`);
    }
  } catch (error) {
    console.error("Error handling role:", error);
    message.error(
      `An error occurred while ${isEditing.value ? "updating" : "creating"} the role`
    );
  } finally {
    isLoading.value = false;
  }
};

const showDeleteConfirm = (role) => {
  if (role.type === "default") {
    message.warning("Default roles cannot be deleted");
    return;
  }

  deleteModalVisible.value = true;
  deleteRoleId.value = role.id;
};

const deleteRole = async () => {
  deleteModalVisible.value = false;
  isDeleting.value = true;
  try {
    let response;
    let success = false;

    if (isABACEnabled()) {
      // Use new Authz service
      response = await AuthPermissionService.deleteRole(deleteRoleId.value);
      // Authz API returns 204 status for successful delete
      success =
        response.status === 204 || !!response.data || !!response.message;
    } else {
      // Use old DashboardService
      response = await DashboardService.deleteRole(deleteRoleId.value);
      success = response.success;
    }

    if (success) {
      message.success("Role deleted successfully");
      await fetchRoles();
    } else {
      message.error("Failed to delete role");
    }
  } catch (error) {
    console.error("Error deleting role:", error);
    message.error("An error occurred while deleting the role");
  } finally {
    isDeleting.value = false;
  }
};

watch(
  roleModalVisible,
  (isVisible) => {
    if (isVisible) {
      nextTick(() => {
        setTimeout(() => {
          if (roleNameInputRef?.value) {
            roleNameInputRef.value?.focus();
          }
        }, focusDelay);
      });
    }
  },
  { immediate: true }
);

onMounted(() => {
  fetchRoles();
  localStorage.setItem("settingsActiveKey", "2");
  eventBus.on("permission-closed", closePermissionManagement);
});

onUnmounted(() => {
  eventBus.off("permission-closed", closePermissionManagement);
});
</script>
